import { useCallback, useEffect, useRef, useState } from 'react';

export const HANDLE_HEIGHT = 48;
const DEFAULT_EXPANDED_HEIGHT = 360;
const MIN_EXPANDED_HEIGHT = HANDLE_HEIGHT + 80;
const TOP_GAP = 56;
const COLLAPSE_OVERSHOOT = 20;

interface UseDrawerResizeOptions {
  onCollapse: () => void;
}

interface GripProps {
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
}

interface UseDrawerResizeResult {
  expandedHeight: number;
  isResizing: boolean;
  gripProps: GripProps;
}

export const useDrawerResize = ({
  onCollapse,
}: UseDrawerResizeOptions): UseDrawerResizeResult => {
  const [expandedHeight, setExpandedHeight] = useState(
    DEFAULT_EXPANDED_HEIGHT,
  );
  const [isResizing, setIsResizing] = useState(false);
  const dragStateRef = useRef<{ startY: number; startHeight: number } | null>(
    null,
  );

  const clampHeight = useCallback((value: number) => {
    const max =
      typeof window !== 'undefined'
        ? Math.max(MIN_EXPANDED_HEIGHT, window.innerHeight - TOP_GAP)
        : value;
    return Math.min(Math.max(value, MIN_EXPANDED_HEIGHT), max);
  }, []);

  const stopResize = useCallback(() => {
    dragStateRef.current = null;
    setIsResizing(false);
    if (typeof document !== 'undefined') {
      document.body.style.userSelect = '';
    }
  }, []);

  const handleResizeMove = useCallback(
    (clientY: number) => {
      const state = dragStateRef.current;
      if (!state) return;
      const raw = state.startHeight + (state.startY - clientY);
      if (raw < MIN_EXPANDED_HEIGHT - COLLAPSE_OVERSHOOT) {
        onCollapse();
        setExpandedHeight(DEFAULT_EXPANDED_HEIGHT);
        stopResize();
        return;
      }
      setExpandedHeight(clampHeight(raw));
    },
    [clampHeight, stopResize, onCollapse],
  );

  useEffect(() => {
    if (!isResizing) return undefined;
    const onMouseMove = (e: MouseEvent) => handleResizeMove(e.clientY);
    const onMouseUp = () => stopResize();
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleResizeMove(e.touches[0].clientY);
    };
    const onTouchEnd = () => stopResize();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isResizing, handleResizeMove, stopResize]);

  const startResize = useCallback((clientY: number) => {
    dragStateRef.current = { startY: clientY, startHeight: 0 };
    setExpandedHeight(prev => {
      if (dragStateRef.current) {
        dragStateRef.current.startHeight = prev;
      }
      return prev;
    });
    setIsResizing(true);
    if (typeof document !== 'undefined') {
      document.body.style.userSelect = 'none';
    }
  }, []);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      startResize(e.clientY);
    },
    [startResize],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      e.stopPropagation();
      startResize(touch.clientY);
    },
    [startResize],
  );

  const onDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setExpandedHeight(clampHeight(DEFAULT_EXPANDED_HEIGHT));
    },
    [clampHeight],
  );

  return {
    expandedHeight,
    isResizing,
    gripProps: { onMouseDown, onTouchStart, onDoubleClick },
  };
};
